import React, { useEffect, useRef, useState } from "react";

// ==================== MODE CONFIGURATION ====================
// Switch between DEV and PROD manually
const MODE = "DEV"; // Options: 'DEV' or 'PROD'
// const MODE = 'PROD'; // Uncomment for production

// ==================== CONFIGURATION ====================
const CONFIG = {
  animation: {
    duration: 3000, // Total animation duration (ms)
    fadeInDuration: 800,
    fadeOutDuration: 1000,
    textDelay: 1200,
  },
  fireworks: {
    launchInterval: 400,
    particleCount: { min: 60, max: 100 },
    gravity: 0.04,
    fadeRate: 0.012,
  },
  confetti: {
    count: 80,
    gravity: 0.08,
    drift: 0.5,
  },
  colors: {
    primary: ["#667eea", "#764ba2", "#f093fb", "#4facfe"],
    secondary: ["#ffd89b", "#19547b", "#a8edea", "#fed6e3"],
    gold: ["#f9d423", "#ff4e50", "#ffd89b", "#96e6a1"],
  },
  prod: {
    cooldownHours: 3, // Hours to wait before showing again
    storageKey: "newYearCelebration",
  },
};

// ==================== CELEBRATION CANVAS ====================
const CelebrationCanvas = ({ onComplete, onSkip }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      onComplete?.();
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d", { alpha: true });

    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };

    resize();
    window.addEventListener("resize", resize);

    // ==================== UTILITIES ====================
    const random = (min, max) => Math.random() * (max - min) + min;
    const randomInt = (min, max) => Math.floor(random(min, max));
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const easeInOutCubic = (t) =>
      t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    // ==================== PARTICLE SYSTEMS ====================
    const fireworks = [];
    const particles = [];
    const confetti = [];
    let nextLaunch = 0;

    // Firework Rocket Class
    class Firework {
      constructor() {
        this.x = random(width * 0.2, width * 0.8);
        this.y = height;
        this.targetY = random(height * 0.15, height * 0.35);
        this.vx = random(-0.5, 0.5);
        this.vy = random(-12, -9);
        this.color =
          CONFIG.colors.primary[randomInt(0, CONFIG.colors.primary.length)];
        this.trail = [];
        this.exploded = false;
      }

      update() {
        if (this.exploded) return true;

        this.trail.push({ x: this.x, y: this.y, alpha: 1 });
        this.trail.forEach((t) => (t.alpha -= 0.08));
        this.trail = this.trail.filter((t) => t.alpha > 0);

        this.x += this.vx;
        this.y += this.vy;
        this.vy += CONFIG.fireworks.gravity * 0.6;

        if (this.y <= this.targetY) {
          this.explode();
          return true;
        }
        return false;
      }

      explode() {
        this.exploded = true;
        const count = randomInt(
          CONFIG.fireworks.particleCount.min,
          CONFIG.fireworks.particleCount.max
        );
        const palette =
          Math.random() > 0.5 ? CONFIG.colors.primary : CONFIG.colors.gold;

        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 2 * i) / count;
          const speed = random(2, 6);
          particles.push({
            x: this.x,
            y: this.y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            size: random(2, 4),
            color: palette[randomInt(0, palette.length)],
            alpha: 1,
            decay: CONFIG.fireworks.fadeRate,
          });
        }
      }

      draw(ctx) {
        if (this.exploded) return;

        // Draw soft trail
        this.trail.forEach((point, i) => {
          ctx.globalAlpha = point.alpha * 0.4;
          ctx.fillStyle = this.color;
          ctx.beginPath();
          ctx.arc(point.x, point.y, 2, 0, Math.PI * 2);
          ctx.fill();
        });

        // Draw rocket head with subtle glow
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 12;
        ctx.shadowColor = this.color;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    // Confetti Class
    class Confetti {
      constructor() {
        this.x = random(0, width);
        this.y = random(-height * 0.2, 0);
        this.vx = random(-CONFIG.confetti.drift, CONFIG.confetti.drift);
        this.vy = random(1, 3);
        this.rotation = random(0, Math.PI * 2);
        this.rotationSpeed = random(-0.1, 0.1);
        this.size = random(6, 12);
        this.color =
          CONFIG.colors.secondary[randomInt(0, CONFIG.colors.secondary.length)];
        this.alpha = random(0.6, 1);
        this.shape = Math.random() > 0.5 ? "rect" : "circle";
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vy += CONFIG.confetti.gravity;
        this.rotation += this.rotationSpeed;
        this.vx += random(-0.1, 0.1);

        if (this.y > height + 20) {
          this.y = -20;
          this.x = random(0, width);
        }
      }

      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rotation);
        ctx.fillStyle = this.color;

        if (this.shape === "rect") {
          ctx.fillRect(-this.size / 2, -this.size / 2, this.size, this.size);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.restore();
      }
    }

    // Initialize confetti
    for (let i = 0; i < CONFIG.confetti.count; i++) {
      confetti.push(new Confetti());
    }

    // ==================== ANIMATION LOOP ====================
    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Launch fireworks periodically
      if (elapsed < 6000 && elapsed > nextLaunch) {
        fireworks.push(new Firework());
        nextLaunch = elapsed + CONFIG.fireworks.launchInterval;
      }

      // Update and draw confetti (subtle background effect)
      confetti.forEach((c) => {
        c.update();
        c.draw(ctx);
      });

      // Update and draw fireworks
      for (let i = fireworks.length - 1; i >= 0; i--) {
        const fw = fireworks[i];
        if (fw.update()) {
          fireworks.splice(i, 1);
        } else {
          fw.draw(ctx);
        }
      }

      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vx *= 0.98;
        p.vy += CONFIG.fireworks.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        ctx.globalAlpha = p.alpha;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      ctx.globalAlpha = 1;

      // Complete animation after duration
      if (elapsed > CONFIG.animation.duration) {
        onComplete?.();
        return;
      }

      rafRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onComplete]);

  return <canvas ref={canvasRef} className="celebration-canvas" />;
};

// ==================== TEXT OVERLAY ====================
const TextOverlay = ({ show, onSkip }) => {
  const [phase, setPhase] = useState("entering");

  useEffect(() => {
    const enterTimer = setTimeout(() => setPhase("visible"), 800);
    const exitTimer = setTimeout(() => setPhase("exiting"), 2000);

    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, []);

  return (
    <div className={`celebration-overlay celebration-overlay--${phase}`}>
      <div className="celebration-card">
        <div className="celebration-sparkle">✨</div>
        <h1 className="celebration-title">Happy New Year 2025</h1>
        <p className="celebration-subtitle">
          Wishing you success and happiness ahead!
        </p>
        <div className="celebration-sparkle celebration-sparkle--right">✨</div>
      </div>

      <button
        className="celebration-skip"
        onClick={onSkip}
        aria-label="Skip celebration"
      >
        Skip
      </button>
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const NewYearCelebration = () => {
  const [active, setActive] = useState(() => {
    // DEV mode: always show
    if (MODE === "DEV") {
      return true;
    }

    // PROD mode: check localStorage
    try {
      const stored = localStorage.getItem(CONFIG.prod.storageKey);

      if (!stored) {
        // First time running - show celebration
        return true;
      }

      const data = JSON.parse(stored);
      const now = Date.now();

      // Check if cooldown period has passed
      if (now >= data.nextRunTime) {
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error reading celebration data:", error);
      return true; // Show on error to be safe
    }
  });

  useEffect(() => {
    // Only update localStorage in PROD mode when celebration is active
    if (MODE === "PROD" && active) {
      try {
        const now = Date.now();
        const cooldownMs = CONFIG.prod.cooldownHours * 60 * 60 * 1000;

        const celebrationData = {
          lastRunTime: now,
          nextRunTime: now + cooldownMs,
        };

        localStorage.setItem(
          CONFIG.prod.storageKey,
          JSON.stringify(celebrationData)
        );
      } catch (error) {
        console.error("Error saving celebration data:", error);
      }
    }
  }, [active]);

  const handleComplete = () => {
    setActive(false);
  };

  const handleSkip = () => {
    setActive(false);
  };

  if (!active) return null;

  return (
    <div className="new-year-celebration">
      <CelebrationCanvas onComplete={handleComplete} onSkip={handleSkip} />
      <TextOverlay show={active} onSkip={handleSkip} />
      <style>{`
        /* ==================== BASE STYLES ==================== */
        .new-year-celebration {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          z-index: 9999;
          pointer-events: none;
        }

        .celebration-canvas {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.15);
        }

        /* ==================== TEXT OVERLAY ==================== */
        .celebration-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 800ms cubic-bezier(0.4, 0, 0.2, 1);
        }

        .celebration-overlay--entering {
          opacity: 0;
        }

        .celebration-overlay--visible {
          opacity: 1;
        }

        .celebration-overlay--exiting {
          opacity: 0;
          transition-duration: 1000ms;
        }

        /* ==================== GLASSMORPHISM CARD ==================== */
        .celebration-card {
          position: relative;
          background: rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border-radius: 24px;
          padding: 48px 64px;
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.2) inset,
            0 20px 60px rgba(102, 126, 234, 0.15);
          text-align: center;
          max-width: 600px;
          margin: 0 24px;
          animation: cardFloat 3s ease-in-out infinite;
        }

        @keyframes cardFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        /* ==================== TYPOGRAPHY ==================== */
        .celebration-title {
          font-family: 'Segoe UI', 'Inter', -apple-system, system-ui, sans-serif;
          font-size: clamp(32px, 5vw, 48px);
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin: 0 0 16px 0;
          letter-spacing: -0.5px;
          line-height: 1.2;
        }

        .celebration-subtitle {
          font-family: 'Segoe UI', 'Inter', -apple-system, system-ui, sans-serif;
          font-size: clamp(16px, 2.5vw, 20px);
          font-weight: 500;
          color: rgba(255, 255, 255, 0.95);
          margin: 0;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
          line-height: 1.5;
        }

        /* ==================== DECORATIVE ELEMENTS ==================== */
        .celebration-sparkle {
          position: absolute;
          top: 20px;
          left: 20px;
          font-size: 32px;
          animation: sparkleRotate 4s linear infinite;
        }

        .celebration-sparkle--right {
          left: auto;
          right: 20px;
          animation-delay: 2s;
        }

        @keyframes sparkleRotate {
          0%, 100% { transform: rotate(0deg) scale(1); }
          25% { transform: rotate(90deg) scale(1.2); }
          50% { transform: rotate(180deg) scale(1); }
          75% { transform: rotate(270deg) scale(1.2); }
        }

        /* ==================== SKIP BUTTON ==================== */
        .celebration-skip {
          position: absolute;
          top: 24px;
          right: 24px;
          padding: 10px 20px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          color: white;
          font-family: 'Segoe UI', 'Inter', -apple-system, system-ui, sans-serif;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          pointer-events: auto;
          transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .celebration-skip:hover {
          background: rgba(255, 255, 255, 0.25);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
        }

        .celebration-skip:active {
          transform: translateY(0);
        }

        /* ==================== RESPONSIVE DESIGN ==================== */
        @media (max-width: 768px) {
          .celebration-card {
            padding: 32px 28px;
            border-radius: 20px;
          }

          .celebration-sparkle {
            font-size: 24px;
            top: 12px;
            left: 12px;
          }

          .celebration-sparkle--right {
            right: 12px;
          }

          .celebration-skip {
            top: 16px;
            right: 16px;
            padding: 8px 16px;
            font-size: 13px;
          }
        }

        @media (max-width: 480px) {
          .celebration-card {
            padding: 24px 20px;
            margin: 0 16px;
          }
        }

        /* ==================== ACCESSIBILITY ==================== */
        @media (prefers-reduced-motion: reduce) {
          .celebration-card,
          .celebration-sparkle,
          .celebration-skip {
            animation: none !important;
            transition: none !important;
          }
        }

        /* ==================== PRINT STYLES ==================== */
        @media print {
          .new-year-celebration {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default NewYearCelebration;
